import axios from 'axios';
import { Activity } from '../types/Activity';
import { Task, TaskLocation, TaskLocationType, TaskStage,  } from '../types/Task';
import { QuantifiedTask, TaskCosting } from '../types/Task';
import { convertDateFields, convertTaskDates } from '../utils/dateUtils';
import { taskService as projectTaskService } from './taskService';

// New interfaces for task creation/update
interface TaskCostingInput {
  baseRate: number;
  rateMultiplier: number;
  rateAdjustment: number;
  totalValue: number;
  actualTotalValue?: number | null;
  rateAdjustmentComments?: string | null;
  taskType: 'Unit Rate' | 'Lump Sum';
  costType: 'Fixed Cost' | 'Cost Plus';
  labourMarkup?: number;
  materialMarkup?: number;
  subcontractorMarkup?: number;
  vehicleMarkup?: number;
  hiredEquipmentMarkup?: number;
}

export interface TaskCreateInput {
  id?: number;
  activityId: number;
  activityNumber: string;
  priority: string;
  identifiedDate: Date | null;
  description?: string | null;
  instructions?: string | null;
  name?: string | null;
  completionDate: Date | null;
  contractYear: string;
  projectId?: number;
  estimatedEffortHours?: number | null;
  plannedCompletionDate?: Date | null;
  foremanArea?: number | null;
  contractArea?: number | null;
  comments?: string | null;
  status?: string;
  taskType?: string;
  type?: string;
  quantifiedTasks: {
    identifiedQuantity: number;
    unitOfMeasure: string;
  }[];
  taskLocations: {
    type: TaskLocationType;
    latitude: number;
    longitude: number;
    assetId?: number | null;
  }[];
  taskCostings: TaskCostingInput[];
  taskHistory?: {
    summary: string;
    comments: string;
    stage: string;
    external: boolean;
  }[];
  assignedEmployeeIds?: number[];
  assignedVehicleUnitNumbers?: string[];
}

// Add new interfaces for comment and stage
export interface TaskCommentInput {
  comments: string;
  stage: string;
  external?: boolean;
  userId?: number;
  summary?: string;
}

export interface TaskStageInput {
  stage: string;
  comments?: string;
  userId?: number;
}

/** Prefer server `{ error }` (and common fallbacks) from Axios responses. */
function getAxiosErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { error?: string; message?: string; details?: string }
      | string
      | undefined;
    if (typeof data === 'string' && data.trim()) {
      return data.trim();
    }
    if (data && typeof data === 'object') {
      const msg = data.error || data.message || data.details;
      if (typeof msg === 'string' && msg.trim()) {
        return msg.trim();
      }
    }
    if (!error.response) {
      return 'Network error. Please check your connection and try again.';
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }
  return fallback;
}

interface CompleteTaskInput {
  completionAccomplishment: number;
  completionComments: string;
  userId?: number;
  stage: string;
  completionDate?: Date | string;
  taskLocations: Array<{
    type: string;
    latitude: number;
    longitude: number;
  }>;
  actualTotalValue?: number;
}

// Add these interfaces
interface RequestCorrectionInput {
  comments: string;
  userId?: number;
  taskId: number;
}

interface CloseTaskInput {
  comments: string;
  userId?: number;
  taskId: number;
}

interface ReopenTaskInput {
  comments: string;
  userId?: number;
  taskId: number;
}

interface AddHistoryCommentInput {
  comments: string;
  userId?: number;
  isExternal: boolean;
  taskId: number;
}

// Add this interface
interface CancelTaskInput {
  comments: string;
  userId?: number;
  taskId: number;
}

// Add this interface near the other interfaces
interface UpdateTaskStatusInput {
  comments: string;
  userId?: number;
  taskId: number;
  status: TaskStage;
}

// Add this interface
interface BulkUpdateStatusInput {
  taskIds: number[];
  status: TaskStage;
  comments: string;
  userId?: number;
}

// Add interface for paginated response
export interface TaskTotals {
  totalExpectedAccomplishment: number;
  totalActualAccomplishment: number;
  totalExpectedRevenue: number;
  totalActualValue: number;
  totalCosts: number;
}

interface PaginatedTasksResponse {
  data: Task[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  /**
   * No longer returned by GET /tasks/quantified - computing it there blocked the rows on
   * the cost aggregation. Kept optional so older callers still compile; use
   * getQuantifiedTaskTotals() instead.
   */
  totals?: TaskTotals;
}

/**
 * Add interface for task query parameters.
 *
 * Every filter is a multiselect on the QP Tracking screen, so each of these accepts a list as well
 * as the bare value older callers (the three home-page KPI hooks) still pass. Lists are sent
 * comma-joined; the server accepts a bare value, a comma-joined list or a repeated key.
 */
interface TaskQueryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: string | string[];
  serviceArea?: string | string[];
  foremanArea?: string | string[];
  contractYear?: string | string[];
  activityNumber?: string | string[];
  activityName?: string | string[];
  projectId?: string | string[];
  costType?: string | string[];
  plannedStartDateFrom?: string;
  plannedStartDateTo?: string;
  plannedCompletionDateFrom?: string;
  plannedCompletionDateTo?: string;
  search?: string;
  /** When set, list returns only this task (e.g. from Work ID screen link). */
  taskId?: number;
}

// Add interface for filter options response
interface FilterOptionsResponse {
  serviceAreas: Array<{ id: number; name: string }>;
  foremanAreas: Array<{ 
    id: number; 
    name: string;
    serviceAreaId: number;
    serviceAreaName: string;
  }>;
  contractYears: string[];
  activities: Array<{ 
    activityName: string; 
    activityNumber: string;
    serviceAreaId: number;
    serviceAreaName: string;
  }>;
  projects: Array<{ 
    id: number; 
    name: string; 
    projectNumber: string;
    serviceAreaId: number;
    serviceAreaName: string;
  }>;
  statuses: string[];
  costTypes: string[];
}

// Add this interface for updating actual accomplishment
interface UpdateActualAccomplishmentInput {
  completionAccomplishment: number;
  completionDate?: Date | string;
  startDate?: Date | string;
  userId?: number;
  actualTotalValue?: number;
  manuallyEdited?: boolean; // Flag to indicate if actualTotalValue was manually edited
}

export interface QuantifiedCompletedCountsResponse {
  thisWeek: number;
  lastWeek: number;
  weekBoundaries?: {
    thisWeekStart: string;
    thisWeekEnd: string;
    lastWeekStart: string;
    lastWeekEnd: string;
  };
}

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Builds the shared filter query string for the quantified task endpoints.
 *
 * GET /tasks/quantified and GET /tasks/quantified/totals must be filtered identically -
 * the totals describe the rows, so any divergence shows up as a summary card that
 * silently disagrees with the table beneath it.
 */
function buildQuantifiedTaskQueryParams(params: TaskQueryParams): URLSearchParams {
  const queryParams = new URLSearchParams();
  
  // Add pagination parameters
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());
  
  // Add sort parameters (client column names only)
  if (params.sortBy) {
    queryParams.append('sortBy', params.sortBy);
    queryParams.append('sortOrder', (params.sortOrder || 'desc') as string);
  }
  
  // Add filter parameters. appendList drops empty selections entirely so an empty multiselect
  // means "no filter" on the server rather than a filter that matches nothing.
  const appendList = (name: string, value: string | string[] | undefined) => {
    if (value == null) return;
    const values = (Array.isArray(value) ? value : [value])
      .map((v) => String(v).trim())
      .filter(Boolean);
    if (values.length > 0) queryParams.append(name, values.join(','));
  };

  appendList('status', params.status);
  appendList('serviceArea', params.serviceArea);
  appendList('foremanArea', params.foremanArea);
  appendList('contractYear', params.contractYear);
  if (params.activityNumber) {
    const numbers = Array.isArray(params.activityNumber) ? params.activityNumber : [params.activityNumber];
    for (const number of numbers) {
      const trimmed = String(number).trim();
      if (trimmed) queryParams.append('activityNumber', trimmed);
    }
  } else if (params.activityName) {
    // Backward-compatibility path for older callers.
    const names = Array.isArray(params.activityName) ? params.activityName : [params.activityName];
    for (const name of names) {
      const trimmed = String(name).trim();
      if (trimmed) queryParams.append('activityName', trimmed);
    }
  }
  appendList('projectId', params.projectId);
  appendList('costType', params.costType);
  if (params.plannedStartDateFrom) queryParams.append('plannedStartDateFrom', params.plannedStartDateFrom);
  if (params.plannedStartDateTo) queryParams.append('plannedStartDateTo', params.plannedStartDateTo);
  if (params.plannedCompletionDateFrom) queryParams.append('plannedCompletionDateFrom', params.plannedCompletionDateFrom);
  if (params.plannedCompletionDateTo) queryParams.append('plannedCompletionDateTo', params.plannedCompletionDateTo);
  if (params.search) queryParams.append('search', params.search);
  if (params.taskId != null && params.taskId > 0) queryParams.append('taskId', params.taskId.toString());

  return queryParams;
}

export const taskService = {
  async getQuantifiedTasks(): Promise<Task[]> {
    try {
      const response = await axios.get(`${BASE_URL}/tasks/quantified`);
      
      // Handle both old format (array) and new format (paginated object)
      let tasks: Task[];
      if (Array.isArray(response.data)) {
        // Old format - direct array
        tasks = response.data;
      } else {
        // New format - paginated response
        tasks = response.data.data || [];
      }
      
      // Transform the data to ensure all effort arrays exist and convert date strings to Date objects
      const data = tasks.map((task: Task) => ({
        ...convertTaskDates(task),
        activity: task.activities?.[0], 
        labours: task.labours || [],
        materials: task.materials || [],
        vehicles: task.vehicles || [],
        subcontractors: task.subcontractors || []
      }));
      
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Failed to fetch tasks');
      }
      throw error;
    }
  },

  async getQuantifiedCompletedCounts(params?: { foremanArea?: string | string[]; serviceArea?: string }): Promise<QuantifiedCompletedCountsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.foremanArea) {
        queryParams.append('foremanArea', Array.isArray(params.foremanArea) ? params.foremanArea.join(',') : params.foremanArea);
      }
      if (params?.serviceArea) {
        queryParams.append('serviceArea', params.serviceArea);
      }
      const queryString = queryParams.toString();
      const url = `${BASE_URL}/tasks/quantified/completed-counts${queryString ? `?${queryString}` : ''}`;
      const response = await axios.get(url);
      if (!response.data || typeof response.data.thisWeek !== 'number' || typeof response.data.lastWeek !== 'number') {
        throw new Error('Invalid response: expected thisWeek and lastWeek');
      }
      return {
        thisWeek: response.data.thisWeek,
        lastWeek: response.data.lastWeek,
        weekBoundaries: response.data.weekBoundaries,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Failed to fetch completed counts');
      }
      throw error;
    }
  },

  async getQuantifiedTasksPaginated(params: TaskQueryParams = {}): Promise<PaginatedTasksResponse> {
    try {
      const queryParams = buildQuantifiedTaskQueryParams(params);
      
      const url = `${BASE_URL}/tasks/quantified?${queryParams.toString()}`;
      
      const response = await axios.get(url);
      
      // Validate the response structure
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid response: expected object');
      }
      
      if (!response.data.data || !Array.isArray(response.data.data)) {
        throw new Error('Invalid response: missing or invalid data array');
      }
      
      if (!response.data.pagination || typeof response.data.pagination !== 'object') {
        throw new Error('Invalid response: missing pagination information');
      }
      
      // Transform the data to ensure all effort arrays exist and convert date strings to Date objects
      const transformedData = {
        ...response.data,
        data: response.data.data.map((task: Task) => {
          const convertedTask = {
            ...convertTaskDates(task),
            activity: task.activities?.[0], 
            labours: task.labours || [],
            materials: task.materials || [],
            vehicles: task.vehicles || [],
            subcontractors: task.subcontractors || []
          };
          

          
          return convertedTask;
        })
      };
      
      return transformedData;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Failed to fetch paginated tasks');
      }
      throw error;
    }
  },

  /**
   * Summary totals for the quantified task list.
   *
   * Separate from getQuantifiedTasksPaginated so the table can render as soon as the rows
   * arrive. The cost aggregation behind this resolves every matching task id before
   * summing, so with no filters applied it is far slower than the list itself - loading it
   * alongside rather than ahead of the rows is what keeps the screen responsive.
   *
   * Takes the same params as the list so both are filtered identically.
   */
  async getQuantifiedTaskTotals(params: TaskQueryParams = {}): Promise<TaskTotals> {
    try {
      const queryParams = buildQuantifiedTaskQueryParams(params);
      // Paging and sorting do not change an aggregate over the whole filtered set.
      // Dropping them keeps the URL stable while the user pages through results.
      queryParams.delete('page');
      queryParams.delete('limit');
      queryParams.delete('sortBy');
      queryParams.delete('sortOrder');

      const url = `${BASE_URL}/tasks/quantified/totals?${queryParams.toString()}`;
      const response = await axios.get(url);

      if (!response.data?.totals) {
        throw new Error('Invalid response: missing totals');
      }

      return response.data.totals;
    } catch (error) {
      throw new Error(getAxiosErrorMessage(error, 'Failed to fetch task totals'));
    }
  },

  async getFilterOptions(): Promise<FilterOptionsResponse> {
    try {
      const url = `${BASE_URL}/tasks/quantified/filter-options`;
      
      const response = await axios.get(url);
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Failed to fetch filter options');
      }
      throw error;
    }
  },

  async getTask(id: number): Promise<Task> {
    try {
      const response = await axios.get(`${BASE_URL}/tasks/quantified/${id}`);
      
      // Log the response to verify Work Planning fields are present
      console.log('[taskService.getTask] Response data:', {
        taskId: response.data.id,
        assignedEmployees: response.data.assignedEmployees,
        assignedEmployeesCount: response.data.assignedEmployees?.length || 0,
        estimatedEffortHours: response.data.estimatedEffortHours,
        plannedCompletionDate: response.data.plannedCompletionDate
      });
      
      // Transform the data to ensure all necessary fields exist and convert date strings to Date objects
      const task = {
        ...convertTaskDates(response.data),
        activity: response.data.activities?.[0],
        activities: response.data.activities || [],
        labours: response.data.labours || [],
        materials: response.data.materials || [],
        vehicles: response.data.vehicles || [],
        subcontractors: response.data.subcontractors || [],
        taskLocations: response.data.taskLocations || [],
        quantifiedTasks: response.data.quantifiedTasks || [],
        taskCostings: response.data.taskCostings || [],
        assignedEmployees: response.data.assignedEmployees || [], // Explicitly include assignedEmployees
        estimatedEffortHours: response.data.estimatedEffortHours, // Explicitly include estimatedEffortHours
        plannedCompletionDate: response.data.plannedCompletionDate, // Explicitly include plannedCompletionDate
        taskHistory: (response.data.taskHistory || []).map((history: any) => ({
          ...convertDateFields(history, ['createdAt'] as (keyof typeof history)[])
        })),
        taskComments: (response.data.taskComments || []).map((comment: any) => ({
          ...convertDateFields(comment, ['createdAt'] as (keyof typeof comment)[])
        })),
        // Explicitly include efforts so consumers like TaskModal can load Other Costs reliably
        efforts: response.data.efforts || []
      };
      
      // Log the transformed task to verify Work Planning fields are preserved
      console.log('[taskService.getTask] Transformed task:', {
        taskId: task.id,
        assignedEmployees: task.assignedEmployees,
        assignedEmployeesCount: task.assignedEmployees?.length || 0,
        estimatedEffortHours: task.estimatedEffortHours,
        plannedCompletionDate: task.plannedCompletionDate
      });
      
      return task;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Failed to fetch task');
      }
      throw error;
    }
  },

  async createTask(taskData: TaskCreateInput): Promise<Task> {
    try {
      // First get the activity details
      const activityResponse = await axios.get(`${BASE_URL}/activities/${taskData.activityId}`);
      const activity = activityResponse.data;

      // Get the asset details if we have a taskLocation with an assetId
      let assetName = '';
      let assetNumber = '';
      const startLocation = taskData.taskLocations.find(loc => loc.type === 'START' && loc.assetId);
      if (startLocation?.assetId) {
        const assetResponse = await axios.get(`${BASE_URL}/assets/${startLocation.assetId}`);
        const asset = assetResponse.data;
        
        // For non-road assets, try to get the parent asset
        if (asset.assetType.toUpperCase() !== 'ROAD' && asset.parentAsset) {
          // Find the parent asset by assetNumber
          const parentAssetResponse = await axios.get(`${BASE_URL}/assets/search?assetNumber=${asset.parentAsset}`);
          const parentAssets = parentAssetResponse.data;
          if (parentAssets && parentAssets.length > 0) {
            const parentAsset = parentAssets[0];
            assetName = parentAsset.name;
            assetNumber = parentAsset.assetNumber;
          } else {
            // Fallback to the selected asset if parent not found
            assetName = asset.name;
            assetNumber = asset.assetNumber;
          }
        } else {
          // For roads or assets without parent, use the selected asset
          assetName = asset.name;
          assetNumber = asset.assetNumber;
        }
      }

      // Generate the task name
      if (!taskData.name && activity) {
        taskData.name = `${activity.activityName} (#${activity.activityNumber}) on ${assetName} (#${assetNumber})`;
      }

      const response = await axios.post(`${BASE_URL}/tasks/quantified`, taskData);
      return response.data;
    } catch (error) {
      throw new Error(getAxiosErrorMessage(error, 'Failed to create task'));
    }
  },

  async updateFullTask(taskId: string, taskData: TaskCreateInput): Promise<Task> {
    try {
      // Get activity details
      const activityResponse = await axios.get(`${BASE_URL}/activities/${taskData.activityId}`);
      const activity = activityResponse.data;

      // Get asset details if we have a taskLocation with an assetId
      let assetName = '';
      let assetNumber = '';
      const startLocation = taskData.taskLocations.find(loc => loc.type === 'START' && loc.assetId);
      if (startLocation?.assetId) {
        const assetResponse = await axios.get(`${BASE_URL}/assets/${startLocation.assetId}`);
        const asset = assetResponse.data;
        
        // For non-road assets, try to get the parent asset
        if (asset.assetType.toUpperCase() !== 'ROAD' && asset.parentAsset) {
          // Find the parent asset by assetNumber
          const parentAssetResponse = await axios.get(`${BASE_URL}/assets/search?assetNumber=${asset.parentAsset}`);
          const parentAssets = parentAssetResponse.data;
          if (parentAssets && parentAssets.length > 0) {
            const parentAsset = parentAssets[0];
            assetName = parentAsset.name;
            assetNumber = parentAsset.assetNumber;
          } else {
            // Fallback to the selected asset if parent not found
            assetName = asset.name;
            assetNumber = asset.assetNumber;
          }
        } else {
          // For roads or assets without parent, use the selected asset
          assetName = asset.name;
          assetNumber = asset.assetNumber;
        }
      }

      // Generate the task name
      if (activity && !taskData.name) {
        taskData.name = `${activity.activityName} (#${activity.activityNumber}) on ${assetName} (#${assetNumber})`;
      }

      // We'll skip getting the existing task for comparison - it's not essential
      // and can cause the entire operation to fail if there's a session issue
      let existingTask: Task | null = null;
      
      // Format the completion date if it exists
      let formattedCompletionDate = taskData.completionDate;
      if (formattedCompletionDate && typeof formattedCompletionDate === 'string' && !(formattedCompletionDate as string).includes('T')) {
        // Convert YYYY-MM-DD to ISO string with noon UTC to avoid timezone issues
        formattedCompletionDate = new Date(`${formattedCompletionDate}T12:00:00Z`);
      }
      
      // Ensure the ID is included in the request
      const dataWithId = {
        ...taskData,
        completionDate: formattedCompletionDate,
        id: parseInt(taskId),
        activityId: Number(taskData.activityId),
        projectId: taskData.projectId ? Number(taskData.projectId) : null,
        foremanArea: taskData.foremanArea ? Number(taskData.foremanArea) : null,
        contractArea: taskData.contractArea ? Number(taskData.contractArea) : null,
        // Ensure all number fields are properly converted
        quantifiedTasks: taskData.quantifiedTasks.map(qt => ({
          ...qt,
          identifiedQuantity: Number(qt.identifiedQuantity)
        })),
        taskCostings: taskData.taskCostings.map(tc => ({
          ...tc,
          baseRate: Number(tc.baseRate),
          rateMultiplier: Number(tc.rateMultiplier),
          rateAdjustment: Number(tc.rateAdjustment),
          totalValue: Number(tc.totalValue),
          actualTotalValue: tc.actualTotalValue ? Number(tc.actualTotalValue) : null,
          labourMarkup: tc.labourMarkup ? Number(tc.labourMarkup) : 0,
          materialMarkup: tc.materialMarkup ? Number(tc.materialMarkup) : 0,
          subcontractorMarkup: tc.subcontractorMarkup ? Number(tc.subcontractorMarkup) : 0,
          vehicleMarkup: tc.vehicleMarkup ? Number(tc.vehicleMarkup) : 0
        })),
        taskLocations: taskData.taskLocations.map(tl => ({
          ...tl,
          latitude: Number(tl.latitude),
          longitude: Number(tl.longitude),
          assetId: tl.assetId ? Number(tl.assetId) : null
        })),
        assignedEmployeeIds: taskData.assignedEmployeeIds,
        assignedVehicleUnitNumbers: taskData.assignedVehicleUnitNumbers
      };
      
      // Set specific headers to ensure we get JSON back and explicitly use PUT method
      const response = await axios({
        method: 'PUT',
        url: `${BASE_URL}/tasks/quantified/${taskId}`,
        data: dataWithId,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        // Add timestamp to prevent caching
        params: {
          _t: new Date().getTime()
        }
      });
      
      // Check if response.data is a string (HTML) instead of an object (JSON)
      if (typeof response.data === 'string') {
        throw new Error('Session expired or server error. Please refresh the page and try again.');
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          throw new Error('Your session has expired. Please log in again.');
        }
        throw new Error(
          getAxiosErrorMessage(error, `Failed to update task (${error.response?.status ?? 'unknown'})`)
        );
      }
      throw error;
    }
  },

  async deleteTask(id: number): Promise<void> {
    await axios.delete(`${BASE_URL}/tasks/${id}`);
  },

  async updateAccomplishment(
    id: number, 
    completionAccomplishment: number,
    status: 'pending' | 'completed',
    completionComments?: string
  ): Promise<Task> {
    const response = await axios.patch(`${BASE_URL}/tasks/quantified/${id}/accomplishment`, {
      completionAccomplishment,
      status,
      completionComments
    });
    return response.data;
  },

  async addComment(taskId: number, commentData: TaskCommentInput): Promise<Task> {
    try {
      if (!taskId || isNaN(taskId)) {
        throw new Error('Invalid task ID');
      }

      const response = await axios.post(
        `${BASE_URL}/tasks/quantified/${taskId}/comment`, 
        commentData,
        {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.message || 'Failed to add comment');
      }
      throw error;
    }
  },

  async updateStage(taskId: number, stageData: TaskStageInput): Promise<Task> {
    const response = await axios.patch(
      `${BASE_URL}/tasks/quantified/${taskId}/stage`, 
      stageData
    );
    return response.data;
  },

  async updateTask(taskId: number, data: Partial<Task>): Promise<Task> {
    const response = await axios.patch(`${BASE_URL}/tasks/quantified/${taskId}`, data);
    return response.data; 
  },

  async completeTask(taskId: number, data: CompleteTaskInput): Promise<Task> {
    const response = await axios.post(`${BASE_URL}/tasks/quantified/${taskId}/complete`, data);
    return response.data;
  },

  async requestCorrection(taskId: number, data: RequestCorrectionInput): Promise<Task> {
    const response = await axios.post(`${BASE_URL}/tasks/quantified/${taskId}/request-correction`, data);
    return response.data;
  },

  async closeTask(taskId: number, data: CloseTaskInput): Promise<Task> {
    const response = await axios.post(`${BASE_URL}/tasks/quantified/${taskId}/close`, data);
    return response.data;
  },

  async reopenTask(taskId: number, data: ReopenTaskInput): Promise<Task> {
    const response = await axios.post(`${BASE_URL}/tasks/quantified/${taskId}/reopen`, data);
    return response.data;
  },

  async addHistoryComment(taskId: number, data: Omit<AddHistoryCommentInput, 'taskId'>): Promise<Task> {
    try {
      const response = await axios.post(`${BASE_URL}/tasks/quantified/${taskId}/history-comment`, data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.message || 'Failed to add history comment');
      }
      throw error;
    }
  },

  async cancelTask(taskId: number, data: CancelTaskInput): Promise<Task> {
    const response = await axios.post(`${BASE_URL}/tasks/quantified/${taskId}/cancel`, data);
    return response.data;
  },

  async setToInternalReview(taskId: number, data: { comments: string; userId?: number }): Promise<Task> {
    const response = await axios.post(`${BASE_URL}/tasks/quantified/${taskId}/set-internal-review`, data);
    return response.data;
  },

  async updateTaskStatus(taskId: number, data: UpdateTaskStatusInput): Promise<Task> {
    try {
      // Add history comment first
      await this.addHistoryComment(taskId, {
        comments: data.comments,
        userId: data.userId,
        isExternal: false,
      });

      // Then update the task status
      const response = await axios.patch(`${BASE_URL}/tasks/quantified/${taskId}`, {
        status: data.status
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.message || 'Failed to update task status');
      }
      throw error;
    }
  },

  async updateActualAccomplishment(taskId: number, data: UpdateActualAccomplishmentInput): Promise<Task> {
    try {
      // Use the merged route with the flag to indicate this is an actual accomplishment update
      const response = await axios.put(`${BASE_URL}/tasks/quantified/${taskId}`, {
        ...data,
        isActualAccomplishmentUpdate: true
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.message || 'Failed to update actual accomplishment');
      }
      throw error;
    }
  },

  async bulkUpdateStatus(data: BulkUpdateStatusInput): Promise<Task[]> {
    try {
      const response = await axios.post(`${BASE_URL}/tasks/quantified/bulk-status-update`, data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.message || 'Failed to update tasks status');
      }
      throw error;
    }
  },


  async duplicateTasks(
    tasks: Task[],
    names?: (string | undefined)[],
    quantifiedOptions?: { contractYear?: string; status?: string }
  ): Promise<Task[]> {
    try {
      const duplicatedTasks: Task[] = [];
      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];
        const nm = names?.[i];
        const isQuantified = task.type === 'Quantified';
        const options: { name?: string; contractYear?: string; status?: string } = {};
        if (nm != null && nm.trim() !== '') {
          options.name = nm.trim();
        }
        if (isQuantified && quantifiedOptions) {
          if (quantifiedOptions.contractYear?.trim()) {
            options.contractYear = quantifiedOptions.contractYear.trim();
          }
          if (quantifiedOptions.status?.trim()) {
            options.status = quantifiedOptions.status.trim();
          }
        }
        const cloned = await projectTaskService.cloneTask(
          task.id,
          Object.keys(options).length > 0 ? options : undefined
        );
        duplicatedTasks.push(convertTaskDates(cloned));
      }
      return duplicatedTasks;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const body = error.response.data as { error?: string; message?: string; details?: string };
        throw new Error(body?.error || body?.message || body?.details || 'Failed to clone tasks');
      }
      throw error;
    }
  },
};